import { useCallback, useEffect, useMemo, useState } from 'react'
import { capitalize } from 'lodash'
import moment, { Moment } from 'moment'
import { useHistory, useLocation } from 'react-router-dom'

import { useCurrentOrg } from 'auth/hooks'
import { buildCalendarSearch, parseCalendarDateFromSearch } from './calendarUrl'

type SetPeriodState = Moment | ((prev: Moment) => Moment)

export const useCalendarPeriod = (period: string) => {
  const org = useCurrentOrg()
  const history = useHistory()
  const location = useLocation()

  const [firstOfPeriod, setFirstOfPeriodState] = useState(() =>
    parseCalendarDateFromSearch(location.search, org.timeZone, period),
  )

  useEffect(() => {
    setFirstOfPeriodState(parseCalendarDateFromSearch(location.search, org.timeZone, period))
  }, [location.search, org.timeZone, period])

  useEffect(() => {
    if (!new URLSearchParams(location.search).get('date')) {
      history.replace({
        pathname: location.pathname,
        search: `?${buildCalendarSearch(
          moment()
            .tz(org.timeZone)
            .startOf(period as moment.unitOfTime.StartOf),
        )}`,
      })
    }
  }, [history, location.pathname, location.search, org.timeZone, period])

  const setFirstOfPeriod = useCallback(
    (value: SetPeriodState) => {
      setFirstOfPeriodState(prev => {
        const resolved = typeof value === 'function' ? value(prev) : value
        history.replace({
          pathname: location.pathname,
          search: `?${buildCalendarSearch(resolved)}`,
        })
        return resolved
      })
    },
    [history, location.pathname],
  )

  const gotoPrevPeriod = useCallback(() => {
    setFirstOfPeriod(prev => prev.clone().subtract(1, period as moment.unitOfTime.DurationConstructor))
  }, [period, setFirstOfPeriod])

  const gotoNextPeriod = useCallback(() => {
    setFirstOfPeriod(prev => prev.clone().add(1, period as moment.unitOfTime.DurationConstructor))
  }, [period, setFirstOfPeriod])

  const gotoCurrentPeriod = useCallback(() => {
    setFirstOfPeriod(
      moment()
        .tz(org.timeZone)
        .startOf(period as moment.unitOfTime.StartOf),
    )
  }, [org.timeZone, period, setFirstOfPeriod])

  const capitalizedPeriod = capitalize(period)

  return useMemo(() => {
    return {
      [`firstOf${capitalizedPeriod}`]: firstOfPeriod,
      [`setFirstOf${capitalizedPeriod}`]: setFirstOfPeriod,
      [`gotoPrev${capitalizedPeriod}`]: gotoPrevPeriod,
      [`gotoNext${capitalizedPeriod}`]: gotoNextPeriod,
      [`gotoCurrent${capitalizedPeriod}`]: gotoCurrentPeriod,
    } as any
  }, [capitalizedPeriod, firstOfPeriod, setFirstOfPeriod, gotoPrevPeriod, gotoNextPeriod, gotoCurrentPeriod])
}
